// SourceBadge — a brand-colored logo badge for an order's acquisition source.
// Replaces the old ambiguous emoji (📘 "blue book" for Facebook etc.) with the
// real platform logo so an admin can tell at a glance where a lead came from.
// Keys match the canonical traffic_source set from worker/src/lib/trafficSource.ts.
import type { IconType } from 'react-icons';
import {
  SiFacebook,
  SiInstagram,
  SiTiktok,
  SiGoogle,
  SiGoogleads,
  SiOpenai,
  SiSnapchat,
  SiYoutube,
  SiWhatsapp,
  SiX,
  SiPinterest,
} from 'react-icons/si';
import { Globe, HelpCircle } from 'lucide-react';

interface SourceVisual {
  label: string;
  Icon: IconType;
  /** Logo color. */
  fg: string;
  /** Badge background — solid brand color or the Instagram gradient. */
  bg: string;
}

// fg/bg chosen so each logo reads instantly on a light admin table.
const SOURCES: Record<string, SourceVisual> = {
  facebook:  { label: 'Facebook',     Icon: SiFacebook,  fg: '#FFFFFF', bg: '#1877F2' },
  instagram: { label: 'Instagram',    Icon: SiInstagram, fg: '#FFFFFF', bg: 'linear-gradient(45deg,#FEDA75,#FA7E1E,#D62976,#962FBF,#4F5BD5)' },
  tiktok:    { label: 'TikTok',       Icon: SiTiktok,    fg: '#FFFFFF', bg: '#010101' },
  google:    { label: 'Google',       Icon: SiGoogle,    fg: '#FFFFFF', bg: '#4285F4' },
  googleads: { label: 'Google Ads',   Icon: SiGoogleads, fg: '#FFFFFF', bg: '#4285F4' },
  chatgpt:   { label: 'ChatGPT / IA', Icon: SiOpenai,    fg: '#FFFFFF', bg: '#10A37F' },
  snapchat:  { label: 'Snapchat',     Icon: SiSnapchat,  fg: '#111111', bg: '#FFFC00' },
  youtube:   { label: 'YouTube',      Icon: SiYoutube,   fg: '#FFFFFF', bg: '#FF0000' },
  whatsapp:  { label: 'WhatsApp',     Icon: SiWhatsapp,  fg: '#FFFFFF', bg: '#25D366' },
  x:         { label: 'X (Twitter)',  Icon: SiX,         fg: '#FFFFFF', bg: '#000000' },
  pinterest: { label: 'Pinterest',    Icon: SiPinterest, fg: '#FFFFFF', bg: '#E60023' },
  direct:    { label: 'Direct',       Icon: Globe,       fg: '#FFFFFF', bg: '#6B7280' },
  other:     { label: 'Autre',        Icon: HelpCircle,  fg: '#FFFFFF', bg: '#9CA3AF' },
};

export function sourceLabel(source: string | null | undefined): string {
  if (!source) return SOURCES.direct.label;
  return (SOURCES[source] ?? SOURCES.other).label;
}

/** A round brand-logo badge. Null/unknown source → Direct/Autre. */
export function SourceBadge({
  source,
  size = 20,
}: {
  source: string | null | undefined;
  size?: number;
}) {
  const v = !source ? SOURCES.direct : SOURCES[source] ?? SOURCES.other;
  const Icon = v.Icon;
  return (
    <span
      title={`Source : ${v.label}`}
      aria-label={`Source : ${v.label}`}
      className="inline-flex shrink-0 items-center justify-center rounded-full ring-1 ring-black/5"
      style={{ width: size, height: size, background: v.bg }}
    >
      <Icon style={{ color: v.fg }} size={Math.round(size * 0.58)} />
    </span>
  );
}
